import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  User as UserIcon, Palette, CreditCard, Share2, Upload, Save, Image, Trash2,
  Building2, Globe, Phone, Mail, Facebook, Instagram, Linkedin, Twitter, Plus, X, Camera, Users
} from 'lucide-react';
import { AppNavbar } from '../components/AppNavbar';

export const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const logoInputRef = useRef();
  const headshotInputRef = useRef();
  
  // Profile data
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('');
  
  // Business Info
  const [businessName, setBusinessName] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: ''
  });
  
  // Brand kit data
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [headshotUrl, setHeadshotUrl] = useState('');
  const [headshotFile, setHeadshotFile] = useState(null);
  const [primaryColor, setPrimaryColor] = useState('#064E3B');
  const [secondaryColor, setSecondaryColor] = useState('#E5E5E0');
  const [accentColor, setAccentColor] = useState('#D4AF37');
  const [fontPrimary, setFontPrimary] = useState('Playfair Display');
  const [fontSecondary, setFontSecondary] = useState('Manrope');

  // Realtor identity (powers {{agent_name}}, {{agent_phone}}, etc. placeholders)
  const [agentName, setAgentName] = useState('');
  const [agentTitle, setAgentTitle] = useState('');
  const [agentPhone, setAgentPhone] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [agentWebsite, setAgentWebsite] = useState('');
  const [agentLicense, setAgentLicense] = useState('');
  const [brokerage, setBrokerage] = useState('');
  const [brokerageAddress, setBrokerageAddress] = useState('');
  
  // Email Lists
  const [emailLists, setEmailLists] = useState([]);
  const [newListName, setNewListName] = useState('');
  const [newEmails, setNewEmails] = useState('');
  const [showAddList, setShowAddList] = useState(false);
  
  // Billing data
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [billingEmail, setBillingEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchBrandKit();
    fetchBilling();
    fetchEmailLists();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setName(response.data.name || '');
      setEmail(response.data.email || '');
      setCity(response.data.city || '');
      setZipCode(response.data.zip_code || '');
      setCountry(response.data.country || '');
      // Business info
      setBusinessName(response.data.business_name || '');
      setBusinessWebsite(response.data.business_website || '');
      setBusinessPhone(response.data.business_phone || '');
      setBusinessEmail(response.data.business_email || '');
      setSocialLinks(response.data.social_links || {
        facebook: '',
        instagram: '',
        linkedin: '',
        twitter: ''
      });
    } catch (error) {
      console.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandKit = async () => {
    try {
      const response = await apiClient.get('/profile/brand-kit');
      setLogoUrl(response.data.logo_url || '');
      setHeadshotUrl(response.data.headshot_url || '');
      setAgentName(response.data.agent_name || '');
      setAgentTitle(response.data.agent_title || '');
      setAgentPhone(response.data.agent_phone || '');
      setAgentEmail(response.data.agent_email || '');
      setAgentWebsite(response.data.agent_website || '');
      setAgentLicense(response.data.agent_license || '');
      setBrokerage(response.data.brokerage || '');
      setBrokerageAddress(response.data.brokerage_address || '');
      setPrimaryColor(response.data.primary_color || '#064E3B');
      setSecondaryColor(response.data.secondary_color || '#E5E5E0');
      setAccentColor(response.data.accent_color || '#D4AF37');
      setFontPrimary(response.data.font_primary || 'Playfair Display');
      setFontSecondary(response.data.font_secondary || 'Manrope');
    } catch (error) {
      console.error('Failed to fetch brand kit');
    }
  };

  const fetchBilling = async () => {
    try {
      const response = await apiClient.get('/profile/billing');
      setSubscriptionStatus(response.data.subscription_status || 'free');
      setSubscriptionPlan(response.data.subscription_plan);
      setBillingEmail(response.data.billing_email || user?.email || '');
      setPaymentMethod(response.data.payment_method);
    } catch (error) {
      console.error('Failed to fetch billing');
    }
  };

  const fetchEmailLists = async () => {
    try {
      const response = await apiClient.get('/email-lists');
      setEmailLists(response.data.lists || []);
    } catch (error) {
      console.error('Failed to fetch email lists');
    }
  };


  const saveProfile = async () => {
    setSaving(true);
    try {
      await apiClient.put('/profile', {
        name,
        city,
        zip_code: zipCode,
        country,
        business_name: businessName,
        business_website: businessWebsite,
        business_phone: businessPhone,
        business_email: businessEmail,
        social_links: socialLinks
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const saveBrandKit = async () => {
    setSaving(true);
    try {
      await apiClient.put('/profile/brand-kit', {
        logo_url: logoUrl,
        headshot_url: headshotUrl,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        font_primary: fontPrimary,
        font_secondary: fontSecondary,
        agent_name: agentName,
        agent_title: agentTitle,
        agent_phone: agentPhone,
        agent_email: agentEmail,
        agent_website: agentWebsite,
        agent_license: agentLicense,
        brokerage: brokerage,
        brokerage_address: brokerageAddress,
      });
      toast.success('Brand kit updated successfully');
    } catch (error) {
      toast.error('Failed to update brand kit');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoUrl(event.target.result);
        setLogoFile(file);
        toast.success('Logo uploaded! Click "Save Brand Kit" to save.');
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleHeadshotUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Headshot file size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setHeadshotUrl(event.target.result);
        setHeadshotFile(file);
        toast.success('Headshot uploaded! Click "Save Brand Kit" to save.');
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const removeHeadshot = () => {
    setHeadshotUrl('');
    setHeadshotFile(null);
    toast.success('Headshot removed. Click "Save Brand Kit" to save changes.');
  };

  const removeLogo = () => {
    setLogoUrl('');
    setLogoFile(null);
  };

  const addEmailList = async () => {
    if (!newListName.trim()) {
      toast.error('Please enter a list name');
      return;
    }
    
    try {
      const emails = newEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
      await apiClient.post('/email-lists', {
        name: newListName,
        emails: emails
      });
      toast.success('Email list created');
      setNewListName('');
      setNewEmails('');
      setShowAddList(false);
      fetchEmailLists();
    } catch (error) {
      toast.error('Failed to create email list');
    }
  };

  const deleteEmailList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list?')) return;
    
    try {
      await apiClient.delete(`/email-lists/${listId}`);
      toast.success('Email list deleted');
      fetchEmailLists();
    } catch (error) {
      toast.error('Failed to delete email list');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading profile...</p>
      </div>
    );
  }

  // Open a specific tab via ?tab=brand|business|email|billing|profile (deep-link)
  const initialTab = (typeof window !== 'undefined')
    ? (new URLSearchParams(window.location.search).get('tab') || 'profile')
    : 'profile';

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      
      <AppNavbar />

      {/* Content */}
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="profile-title">Settings & Profile</h2>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <UserIcon className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="business" data-testid="tab-business">
              <Building2 className="h-4 w-4 mr-2" />
              Business
            </TabsTrigger>
            <TabsTrigger value="brand" data-testid="tab-brand">
              <Palette className="h-4 w-4 mr-2" />
              Brand Kit
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">
              <Mail className="h-4 w-4 mr-2" />
              Email Lists
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">
              <CreditCard className="h-4 w-4 mr-2" />
              Billing
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6">
            <div className="border border-border/50 rounded-lg p-6 bg-card space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={email}
                      disabled
                      className="bg-muted"
                      data-testid="profile-email-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Location Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="New York"
                      data-testid="profile-city-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip">ZIP Code</Label>
                      <Input
                        id="zip"
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        placeholder="10001"
                        data-testid="profile-zip-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        placeholder="United States"
                        data-testid="profile-country-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-button">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>

          {/* Business Tab */}
          <TabsContent value="business" className="mt-6">
            <div className="border border-border/50 rounded-lg p-6 bg-card space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Business Information</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  This information will be used in your newsletters and marketing materials.
                </p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="businessName">Business Name</Label>
                    <Input
                      id="businessName"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="ABC Realty Group"
                      data-testid="business-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="businessWebsite">Website</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                        <Globe className="h-4 w-4" />
                      </span>
                      <Input
                        id="businessWebsite"
                        value={businessWebsite}
                        onChange={(e) => setBusinessWebsite(e.target.value)}
                        placeholder="www.abcrealty.com"
                        className="rounded-l-none"
                        data-testid="business-website-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="businessPhone">Phone Number</Label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                          <Phone className="h-4 w-4" />
                        </span>
                        <Input
                          id="businessPhone"
                          value={businessPhone}
                          onChange={(e) => setBusinessPhone(e.target.value)}
                          placeholder="(555) 123-4567"
                          className="rounded-l-none"
                          data-testid="business-phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="businessEmail">Business Email</Label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                          <Mail className="h-4 w-4" />
                        </span>
                        <Input
                          id="businessEmail"
                          value={businessEmail}
                          onChange={(e) => setBusinessEmail(e.target.value)}
                          placeholder="contact@abcrealty.com"
                          className="rounded-l-none"
                          data-testid="business-email-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Social Media Profiles</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add your social media links to include social icons in your newsletters.
                </p>
                <div className="space-y-4">
                  <div>
                    <Label>Facebook</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-blue-600 text-white">
                        <Facebook className="h-4 w-4" />
                      </span>
                      <Input
                        value={socialLinks.facebook}
                        onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                        placeholder="https://facebook.com/yourpage"
                        className="rounded-l-none"
                        data-testid="social-facebook-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Instagram</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                        <Instagram className="h-4 w-4" />
                      </span>
                      <Input
                        value={socialLinks.instagram}
                        onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                        placeholder="https://instagram.com/yourprofile"
                        className="rounded-l-none"
                        data-testid="social-instagram-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>LinkedIn</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-blue-700 text-white">
                        <Linkedin className="h-4 w-4" />
                      </span>
                      <Input
                        value={socialLinks.linkedin}
                        onChange={(e) => setSocialLinks({...socialLinks, linkedin: e.target.value})}
                        placeholder="https://linkedin.com/in/yourprofile"
                        className="rounded-l-none"
                        data-testid="social-linkedin-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Twitter/X</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-black text-white">
                        <Twitter className="h-4 w-4" />
                      </span>
                      <Input
                        value={socialLinks.twitter}
                        onChange={(e) => setSocialLinks({...socialLinks, twitter: e.target.value})}
                        placeholder="https://twitter.com/yourhandle"
                        className="rounded-l-none"
                        data-testid="social-twitter-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={saveProfile} disabled={saving} data-testid="save-business-button">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Business Info'}
              </Button>
            </div>
          </TabsContent>

          {/* Brand Kit Tab */}
          <TabsContent value="brand" className="mt-6">
            <div className="border border-border/50 rounded-lg p-6 bg-card space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Your Logo</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload your logo and it will automatically appear on all your templates!
                </p>
                
                {/* Logo Upload Guidelines */}
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">Logo Guidelines for Best Results</h4>
                  <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                    <li>• <strong>Recommended Size:</strong> 500 x 200 pixels (landscape) or 300 x 300 pixels (square)</li>
                    <li>• <strong>Best Format:</strong> PNG with transparent background</li>
                    <li>• <strong>Aspect Ratio:</strong> 2.5:1 (landscape) or 1:1 (square) - Avoid tall/narrow logos</li>
                    <li>• <strong>Max File Size:</strong> 2MB</li>
                    <li>• <strong>Tip:</strong> Use a horizontal logo for flyers/postcards, square for social media</li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  {logoUrl ? (
                    <div className="border border-border/50 rounded-lg p-6 bg-secondary/20">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium">Logo Preview</span>
                        <Button variant="ghost" size="sm" onClick={removeLogo} className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                      <div className="flex justify-center">
                        <img src={logoUrl} alt="Logo preview" className="max-h-32 object-contain" />
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Image className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-sm font-medium mb-1">Click to upload your logo</p>
                      <p className="text-xs text-muted-foreground">PNG (transparent), JPG • Max 2MB • 500x200px recommended</p>
                    </div>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleLogoUpload}
                  />
                  {logoUrl && (
                    <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Change Logo
                    </Button>
                  )}
                </div>
              </div>

              {/* Agent Headshot Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Agent Headshot</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload your professional headshot for use on templates and marketing materials.
                </p>
                
                {/* Headshot Upload Guidelines */}
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">Headshot Guidelines for Best Results</h4>
                  <ul className="text-xs text-green-700 dark:text-green-400 space-y-1">
                    <li>• <strong>Recommended Size:</strong> 400 x 400 pixels (square) minimum</li>
                    <li>• <strong>Best Format:</strong> JPG or PNG</li>
                    <li>• <strong>Aspect Ratio:</strong> 1:1 (square) - Will be cropped to circle on templates</li>
                    <li>• <strong>Max File Size:</strong> 2MB</li>
                    <li>• <strong>Tips:</strong> Center your face, use good lighting, professional attire</li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  {headshotUrl ? (
                    <div className="border border-border/50 rounded-lg p-6 bg-secondary/20">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium">Headshot Preview</span>
                        <Button variant="ghost" size="sm" onClick={removeHeadshot} className="text-destructive" data-testid="remove-headshot-btn">
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                      <div className="flex justify-center">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary/20">
                          <img 
                            src={headshotUrl} 
                            alt="Agent headshot preview" 
                            className="w-full h-full object-cover"
                            data-testid="headshot-preview"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => headshotInputRef.current?.click()}
                      data-testid="headshot-upload-area"
                    >
                      <div className="w-20 h-20 rounded-full bg-muted/50 mx-auto mb-4 flex items-center justify-center">
                        <Camera className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium mb-1">Click to upload your headshot</p>
                      <p className="text-xs text-muted-foreground">JPG, PNG • Max 2MB • 400x400px square recommended</p>
                    </div>
                  )}
                  <input
                    ref={headshotInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleHeadshotUpload}
                    data-testid="headshot-file-input"
                  />
                  {headshotUrl && (
                    <Button variant="outline" size="sm" onClick={() => headshotInputRef.current?.click()} data-testid="change-headshot-btn">
                      <Upload className="h-4 w-4 mr-2" />
                      Change Headshot
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Brand Colors</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="primaryColor">Primary Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        id="primaryColor"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="secondaryColor">Secondary Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        id="secondaryColor"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="accentColor">Accent Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        id="accentColor"
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Fonts</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fontPrimary">Primary Font (Headlines)</Label>
                    <Input
                      id="fontPrimary"
                      value={fontPrimary}
                      onChange={(e) => setFontPrimary(e.target.value)}
                      placeholder="Playfair Display"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fontSecondary">Secondary Font (Body)</Label>
                    <Input
                      id="fontSecondary"
                      value={fontSecondary}
                      onChange={(e) => setFontSecondary(e.target.value)}
                      placeholder="Manrope"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-1">Realtor Identity</h3>
                <p className="text-xs text-muted-foreground mb-4">Auto-fills <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded">{'{{agent_name}}'}</code>, <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded">{'{{agent_phone}}'}</code>, and similar placeholders in every template.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="agentName">Agent Name</Label>
                    <Input id="agentName" value={agentName} onChange={(e) => setAgentName(e.target.value)} placeholder="Jane Doe" data-testid="agent-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="agentTitle">Title</Label>
                    <Input id="agentTitle" value={agentTitle} onChange={(e) => setAgentTitle(e.target.value)} placeholder="Realtor® / Broker / Sales Associate" data-testid="agent-title-input" />
                  </div>
                  <div>
                    <Label htmlFor="agentPhone">Phone</Label>
                    <Input id="agentPhone" value={agentPhone} onChange={(e) => setAgentPhone(e.target.value)} placeholder="(305) 555-0100" data-testid="agent-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="agentEmail">Email</Label>
                    <Input id="agentEmail" type="email" value={agentEmail} onChange={(e) => setAgentEmail(e.target.value)} placeholder="jane@brokerage.com" data-testid="agent-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="agentWebsite">Website</Label>
                    <Input id="agentWebsite" value={agentWebsite} onChange={(e) => setAgentWebsite(e.target.value)} placeholder="janedoe.realestate" data-testid="agent-website-input" />
                  </div>
                  <div>
                    <Label htmlFor="agentLicense">License #</Label>
                    <Input id="agentLicense" value={agentLicense} onChange={(e) => setAgentLicense(e.target.value)} placeholder="SL3412345" data-testid="agent-license-input" />
                  </div>
                  <div>
                    <Label htmlFor="brokerage">Brokerage</Label>
                    <Input id="brokerage" value={brokerage} onChange={(e) => setBrokerage(e.target.value)} placeholder="Coldwell Banker / KW / Compass" data-testid="brokerage-input" />
                  </div>
                  <div>
                    <Label htmlFor="brokerageAddress">Brokerage Address</Label>
                    <Input id="brokerageAddress" value={brokerageAddress} onChange={(e) => setBrokerageAddress(e.target.value)} placeholder="123 Main St, Miami, FL" data-testid="brokerage-address-input" />
                  </div>
                </div>
              </div>

              <Button onClick={saveBrandKit} disabled={saving} data-testid="save-brand-kit-button">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Brand Kit'}
              </Button>
            </div>
          </TabsContent>

          {/* Email Lists Tab - Redirect to full page */}
          <TabsContent value="email" className="mt-6">
            <div className="border border-border/50 rounded-lg p-6 bg-card">
              <div className="text-center py-8">
                <Mail className="h-16 w-16 mx-auto mb-4 text-primary" />
                <h3 className="text-lg font-semibold mb-2">Email Lists Management</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Manage your email subscriber lists with advanced features including tags, segments, notes, and CSV import.
                </p>
                <Link to="/email-lists">
                  <Button data-testid="go-to-email-lists-button">
                    <Users className="h-4 w-4 mr-2" />
                    Go to Email Lists
                  </Button>
                </Link>
              </div>
            </div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="mt-6">
            <div className="border border-border/50 rounded-lg p-6 bg-card space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Subscription Status</h3>
                <div className="flex items-center gap-3 p-4 bg-secondary/20 rounded-lg">
                  <div className={`w-3 h-3 rounded-full ${subscriptionStatus === 'active' ? 'bg-green-500' : subscriptionStatus === 'trial' ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                  <div>
                    <p className="font-medium capitalize">
                      {subscriptionStatus === 'active' ? `${subscriptionPlan || 'Active'} Plan` : 
                       subscriptionStatus === 'trial' ? 'Free Trial' : 'No Active Subscription'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {subscriptionStatus === 'active' ? 'Your subscription is active' :
                       subscriptionStatus === 'trial' ? 'Limited to 3 posts or 7 days' :
                       'Upgrade to access all features'}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Billing Email</h3>
                <Input
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  placeholder="billing@example.com"
                  data-testid="billing-email-input"
                />
              </div>

              <div className="pt-4 border-t border-border/50">
                <Button onClick={() => navigate('/subscription')} data-testid="manage-subscription-button">
                  {subscriptionStatus === 'active' ? 'Manage Subscription' : 'Upgrade Plan'}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
